<template>
    <div class="">
      <TableGutter
        v-if="tab.showTableGutter"
        :selectedAction="isBulkActionEnabled"
        :tools="tab.tableProps.gutterOpts"
        @cc-modal-closed="triggerSetHeaderHeight"
        @cellClicked="eventHandler"
        @re-render-table="rerenderMfTable += 1"
      ></TableGutter>
      <mf-table
        ref="deliveriesTable"
        v-if="tab.tableProps"
        :tableProps="tab.tableProps"
        :tableName='tab.tableName'
        :loadData="getLoadData()"
        :apiMode="getApiMode()"
        :hideGutter="true"
        :orderStage="orderStage"
        :refreshCard="refreshCard"
        @cellClicked="onTableCellClicked"
        :key="rerenderMfTable"
        :isLocallyFiltered="isLocallyFiltered"
        :rowData="rowData"
      >
        <template #delivsField="{ rowData, rowField }">
          <icon-field
          :rowData="rowData"
          :rowField="rowField"
          :shipItemDelivs="getItemDeliveries(rowData)">
          </icon-field>
        </template>
        <template v-slot:shippingStatus="{ rowData: delivery }">
          <field-stage
            v-if="!excludeInternalDelFields(delivery.id)"
            :isEditing="true"
            :shipType="delivery.shipType"
            :value="delivery.status"
            :shippingItems="delivery.items"
            :isStatus="true"
          ></field-stage>
        </template>
        <template #shipBy="{ rowData, rowField }">
          <custom-date-picker
            v-if="!excludeInternalDelFields(rowData.id)"
            :rowData="rowData"
            :rowField="rowField"
            @saveDate="updateShippingLabel(rowData)"/>
        </template>
        <template #deliverBy="{ rowData, rowField }">
          <custom-date-picker
            v-if="!excludeInternalDelFields(rowData.id)"
            :rowData="rowData"
            :rowField="rowField"
            @saveDate="updateShippingLabel(rowData)"/>
        </template>
        <template #notes="{ rowData }">
          <button class="button button-large is-clickable is-block"
            v-if="!excludeInternalDelFields(rowData.id)"
            :class="notesCount(rowData) ? 'is-dark ': 'is-outlined'"
            @click="editNotes(rowData)">
            <h3 class="is-size-3">{{ notesCount(rowData) }}</h3>
            <i class="icon-attachednotes"></i>
          </button>
        </template>
        <template #docs="{ rowData }" >
          <button class="button button-large mr-0  is-clickable is-block"
            v-if="!excludeInternalDelFields(rowData.id)"
            :class="getDocsCount(rowData) ? 'is-dark ': 'is-outlined'"
            @click="editDocs(rowData)">
            <h3 class="is-size-5">{{ getDocsCount(rowData) }}</h3>
            <i class="icon-attacheddocuments"></i>
          </button>
        </template>
      </mf-table>
      <o-loading
        :full-page="true"
        :active="isLoading"
        :can-cancel="false"
      ></o-loading>
    </div>
    <activity-log
      v-if="activityModalOpen"
      :isActive="activityModalOpen"
      :id="activityDetails.id"
      :projectId="activityDetails.projectId"
      :orderName="activityDetails.orderName"
      @update:isActive="activityModalOpen=false"
      ></activity-log>
</template>
  
  <script>
  import {
    defineComponent,
    reactive,
    toRefs,
    ref,
    inject,
  } from 'vue';
  import {
    get,
    isEmpty,
    find,
    findIndex,
    set,
    cloneDeep,
    isUndefined,
    every,
    remove,
    forEach,
    isFunction
  } from 'lodash';
  import uuid from 'uuid/v4';
  import { useStore } from 'vuex';
  import { useToast } from 'vue-toastification';
  import { useRoute, useRouter } from 'vue-router';
  import { ModalProgrammatic } from '@oruga-ui/oruga-next';

  import Shipping from '@/models/Shipping';

  import Print from '@/components/utils/Print';
  import LocalSort from '@/components/utils/LocalSort';
  import MfNotes from '@/components/card-edit/MfNotes';
  import iconField from '@/components/fields/IconField';
  import ActivityLog from '@/components/ActivityLog.vue';
  import TableGutter from '@/components/TableGutter.vue';
  import { DialogProgrammatic } from '@/components/Dialog';
  
  import MfTable from '@/components/table-fields/MfTable.vue';
  import CustomDatePicker from '@/components/CustomDatePicker.vue';
  import CancelDelivery from '@/components/modals/CancelDelivery.vue';
  import ReceiveDelivery from '@/components/modals/ReceiveDelivery.vue';
  import AddItemsToShipping from '@/components/modals/AddItemsToShipping.vue';
  import SplitDeliveries from '@/components/modals/SplitDeliveries.vue';
  import ShippingReceive from '@/components/modals/ShippingReceive.vue';
  import AddFromInventory from '@/components/modals/AddFromInventory.vue';
  import CreateDeliveries from '@/components/modals/CreateDeliveries.vue';
  import GeneralShippingMixin from '@/components/mixins/GeneralShippingMixin';
  import EditDeliveryDetails from '@/components/modals/EditDeliveryDetails.vue';
  import SendDeliveryShippingList from '@/components/modals/SendDeliveryShippingList.vue';
  import AddDelivery from '@/components/modals/AddDelivery.vue';
  import DeliveryItems from '@/components/modals/DeliveryItems.vue';

  export default defineComponent({
    emits:[ 'cellClicked', 'refreshCardViewOnKey'],
    props: {
      rowData: {
        type: Object,
        default: () => {},
      },
      tab: {
        type: Object,
        default: () => {},
      },
      refreshCard: Function,
      deliveryDetails: {
        type: [Array, Object],
        default: () => [],
      },
    },
    components: {
      MfTable,
      MfNotes,
      TableGutter,
      ActivityLog,
      'icon-field': iconField,
      'custom-date-picker': CustomDatePicker,
      DeliveryItems,
    },
    setup(props, { emit }) {
      const emitter = inject('emitter');
      let { rowData } = props;
      const {
        tab, refreshCard
      } = props;
      const store = useStore();
      const toast = useToast();
      const router = useRouter();
      // const emitter = inject('emitter');
      const deliveriesTable = ref(null);
      const route = useRoute();
      const state = reactive({
        tableData: [],
        loadTable: 0,
        orders: [],
        data: {},
        dates: [],
        isLoading: false,
        unscheduledRow: {},
        fetchUnderDelivered: {},
        activityModalOpen: false,
        activityDetails: {
          id: '',
          projectId: '',
          orderName: '',
        },
        mL: {
          items: [],
        }, // for masterLabel shipping order
        updatedMasterShipment: {},
        rerenderMfTable: 0,
        isDeliveryItemsActive:false,
        partialShip: {},
      });
      const {
        addItemFromInv,
        fetchUnscheduledRow,
        fetchUnderDelivered,
        removePartialShipment,
        scheduleDelivery,
        markOrderFinal,
        releaseToInv,
        updateShipment,
      } = GeneralShippingMixin();

      const notesModalActions = inject('notesModalActions')
      const docsModalActions = inject('docsModalActions')

      const getApiMode = () => {
        if (store.state.activeScreen === 'shipping-edit') { return true; }
        return !tab?.tableProps?.isCardView;
      };

      // const loadAsyncData = async () => {
      //   // sort the accessor based on name and asc order
      //   if (!isEmpty(data.updatedMasterShipment) && tab.id === 'deliveries') {
      //     rowData = data.updatedMasterShipment;
      //   }
      //   data.tableData = LocalSort.filter(
      //     get(rowData, tab.accessor || 'partialShipments'),
      //     {
      //       sortField: 'name',
      //       direction: 'asc',
      //     },
      //     true,
      //   );  
      //   /*
      //   * add unscheduled row for deliveries tab (shipping-edit)
      //   * if unscheduled items exist
      //   */
      //   if (route.params.cardId !== 'add' && tab.accessor === 'partialShipments') {
      //     data.mL = rowData; // masterShippingLabel
      //     data.unscheduledRow = fetchUnscheduledRow(rowData, data.tableData);
      //     data.fetchUnderDelivered = fetchUnderDelivered(rowData);
      //     if (!isEmpty(data.fetchUnderDelivered)) {
      //       data.tableData.unshift(data.fetchUnderDelivered);
      //       const tableRow = find(data.tableData, { _id: 'underDelivered' });
      //       set(tableRow, 'underDeliverRowActive', true);
      //     }
      //     if (!isEmpty(data.unscheduledRow)) {
      //       data.tableData.unshift(data.unscheduledRow);
      //       const tableRow = find(data.tableData, { _id: 'unscheduled' });
      //       set(tableRow, 'unscheduledRowActive', true);
      //     }
      //   }
  
      //   // set a unique id for each item
      //  if (tab.accessor === 'partialShipments') {
      //     data.tableData = _.filter(data.tableData, (delivery) => delivery.isEmptyLabel || !_.isEmpty(delivery.items))
      //     for (let i = 0; i < data.tableData.length; i++) {
      //       data.tableData[i].items.forEach((item) => {
      //         set(item, 'uid', uuid());
      //       });
      //     }
      //   }

      //   // filter non-archived data
      //   data.tableData = data.tableData.filter((item) => !item.archived?.value);
      //   return { data: data.tableData, total: data.tableData.length };
      // };
      const loadData = () => {
        let partials = get(rowData, tab.accessor || 'items', []);
        state.mL = props.rowData;
        for (let i = 0; i < partials.length; i++) {
          partials[i].items.forEach((item) => {
            set(item, 'uid', uuid());
          });
        }
        return partials;
      };

      const getLoadData = () => {
         return loadData;
      };

      const refreshTable = async () => {
        state.loadTable++;
        await deliveriesTable.value.refreshTable();
      };
  
      const cancelDelivery = (async (shipment) => {
        
        ModalProgrammatic.open({
            component: CancelDelivery,
            props: {
              rowData: shipment,
              type: 'cancel',
              onConfirm: async () => {
                  try {
                    const order = await shipment.cancelDelivery();
                    if (order) {
                      const getIdx = state.tableData.findIndex((data) => data.id === order.id);
                      remove(state.tableData, { id: order.id });
                      state.tableData.splice(getIdx, 0, order);
                      toast.success('Shipment cancelled successfully');
                      emit('refreshCardViewOnKey', true);
                      await refreshCard();
                    }
                  } catch (e) {
                    console.log('error occured while cancelling shipment', e);
                    toast.error('Failed to cancel shipment !');
                  }
                },
              },
            rootClass: 'modal-xs',
            trapFocus: true,
            canCancel: false,
            
          });
      });
  
  
      // TODO: to create different Util file for cell Clicked.
      const onTableCellClicked = async (params) => {
        const { data, type, event } = params;
        if (type === 'three-dot') {
          return;
        } else if (type === 'scheduleDelivery') {
          state.isLoading = true;
          data.name = `Revived of ${data.name}`;
          await scheduleDelivery(data, refreshTable);
          state.isLoading = false;
        } else if (type === 'activity') {
          state.activityModalOpen = true;
          state.activityDetails = {
            id: data._id,
            projectId: data.project._id,
            orderName: data.name,
          };
        } else if (['printQr', 'printShippingList'].includes(type)) {
          const reqObj = {
            shippingId: data._id,
            projectId: data.project._id,
            tzinfo: new Date().getTimezoneOffset(),
            printSerial: false,
            type: 'shipping',
          };
          state.isLoading = true;
          const qrLabel = await Shipping.getShippingLabel(reqObj);
          const user = store.state.userData;
          if(type === 'printShippingList') {
            try {
              const qrParams = {
                shippingId: data._id,
                projectId: data.project._id,
                tzinfo: new Date().getTimezoneOffset(),
                qrLabel: qrLabel[0].imageData,
                compLogo: qrLabel[0].compLogo,
                userInfo: user.fullName,
                company: {
                  _id: user.company,
                  name: user.companyName,
                }
              }
              const res = await Shipping.generateShippingPdf(qrParams);
              const link = document.createElement('a');
              if(!res.data) {
                throw new Error('No URL found');
              }
              link.href = res.data;
              link.click();
              document.body.removeChild(link);
            } catch (error) {
              console.log('Error: ', error)
              
            } finally {
              state.isLoading = false;
            }
          } else if (type === 'printQr') {
            state.isLoading = false;
            await Print.printQrLabel(qrLabel, user);
          }
        } else if (event === 'addNew') {
          ModalProgrammatic.open({
            component: AddFromInventory,
            props: {
              forShipping: true,
              card: rowData,
            },
            trapFocus: true,
            canCancel: false,
            rootClass: 'modal-sm',
            events: {
              addItem: async (itemsToAdd) => {
                state.isLoading = true;
                const itemsData = Object.values(itemsToAdd);
                if (!isEmpty(itemsData)) {
                  await addItemFromInv(rowData, itemsData);
                }
                state.isLoading = false;
                await refreshCard();
              },
            },
          });
        } else if (type === 'cancelShip') {
          await cancelDelivery(data);
        } else if (type === 'save') {
          let order = cloneDeep(rowData);
          state.isLoading = true;
          set(data, 'isEditing', !data.isEditing);
          if (itemIdx === -1 && !isEmpty(data.parentShipment)) {
            // for delivery row save
            const partialIdx = findIndex(order.partialShipments,
              (partial) => partial._id === data._id);
            order.partialShipments[partialIdx] = data;
            order = await Shipping.update(data);
          } else if (order.shipType === 's-m') {
            order = await Shipping.update(data);
            if (store.state.activeScreen === 'shipping-edit' && params.type === 'save') {
              emitter.emit('refreshCardOnKey');
            }
          }
          data.isLoading = false;
          if (!isUndefined(refreshCard)) {
            await refreshCard();
          }
        } else if (type === 'split') {
          // split delivery
          ModalProgrammatic.open({
            component: SplitDeliveries,
            props: {
              card: data,
              masterShipment: state.mL,
            },
            trapFocus: true,
            canCancel: false,
            rootClass: 'modal-sm',
            events: {
              refreshTab: async (mS) => {
                state.updatedMasterShipment = mS;
                // set updated masterShipment id
                if (mS.shipType === 'm') {
                  router.replace({
                    name: 'shipping-card-edit',
                    params: {
                      cardId: mS._id,
                      projectId: mS.project._id,
                      },
                  });
                }
                await refreshCard();
              },
              close: async () => {
                forEach(get(data, 'items', []), (item) => {
                  item.actualQty = item.quantity;
                  item.selectedQty = 0;
                });
              },
            },
          });
        } else if (type === 'receive') {
          // Receive delivery
          ModalProgrammatic.open({
            component: ReceiveDelivery,
            props: {
              rowData: data,
            },
            trapFocus: true,
            canCancel: false,
            rootClass: 'modal-sm',
            events: {
              close: async () => {
                await refreshCard();
              }
            }
          });
        } else if (type === 'markFinal') {
          try {
            await markOrderFinal(data,
            refreshCard,
            state);
          } catch (e) {
            console.log('error', e)
          } finally {
            data.isLoading = false;
          }
        } else if (type === 'releaseToInv') {
          const deliveryLoc = get(data.delivery, 'currentLocation', {});
          await releaseToInv(deliveryLoc, data, refreshTable, state, async() => {
            await refreshCard();
            state.isLoading = false
          });
          data.isLoading = false;
        } else if (type === 'createDelivery') {
          // Create delivery
          ModalProgrammatic.open({
            component: CreateDeliveries,
            props: {
              card: data,
              masterShipment: state.mL,
            },
            trapFocus: true,
            canCancel: false,
            rootClass: 'modal-sm',
            events: {
              close: async() => {
                await refreshCard();
              }
            },
          });
        } else if (type === 'deleteDelivery') {
          if (['not-started', 'un-scheduled'].includes(data.status)) {
              ModalProgrammatic.open({
                component: CancelDelivery,
                props: {
                  rowData: data,
                  type: 'delete',
                  onConfirm: async () => {
                    const resp = await removePartialShipment(rowData, data);
                    if(data.shipType === 's-m' && resp) {
                      router.replace({ path: `/shipping/${resp.project._id}/${resp.parentShipment}`});
                    } else {
                      await refreshCard();
                    }
                  }
                },
                rootClass: 'modal-xs',
                trapFocus: true,
                canCancel: false,
              });
          }
        } else if (type === 'sendShippingList') {

          ModalProgrammatic.open({
            component: SendDeliveryShippingList,
            props: {
              rowData: data,
            },
            trapFocus: true,
            canCancel: false,
            rootClass: 'modal-sm',
          })
        } else if (type === 'editDelivery') {

          ModalProgrammatic.open({
            component: EditDeliveryDetails,
            props: {
              rowData: data,
            },
            rootClass: 'modal-xs',
            trapFocus: true,
            canCancel: false,
          })
        } else if (type === 'addItems') {
          const emitObj = {
            rowData: data,
            isUnscheduledRow: data.id === 'unscheduled',
            isActive: true,
          }
          emitter.emit('toggle:isDeliveryItems', emitObj)
        } else if(type === 'addDelivery') {

          ModalProgrammatic.open({
            component: AddDelivery,
            props: {
              shipping: props.rowData
            },
            rootClass: 'modal-sm',
            trapFocus: true,
            canCancel: false,
            events: {
              close: async (params) => {
                if(params?.projectId && params?.id) {
                  router.replace({ path: `/shipping/${params.projectId}/${params.id}`});
                } else await refreshCard();
              }
            }
          });
        }
      };

  
      const eventHandler = (event) => {
        emit('cell-clicked', { type: event });
      };
  
      const updateShippingLabel = async (shippinglabel) => {
        try {
          state.isLoading = true;
          await updateShipment(shippinglabel, false);
        } catch (e) {
          console.log('Error: ', e)
        } finally {
          state.isLoading = false;
        }
        await refreshCard();
      };
  
      const getItemDeliveries = (item) => {
        const tempArray = [];
        const res = item._id === '000000000000000000000000' ? props.deliveryDetails[item.catId]
          : props.deliveryDetails[item._id];
        if (!isEmpty(res)) {
          res.forEach((i) => {
            if (i.quantity) {
              tempArray.push({ deliveryName: i.slName, deliveryItemQty: i.quantity });
            }
          });
        }
        return tempArray;
      };

      const notesCount = (rowData) => {
        return rowData.delivery.notes?.length ? 1 : 0;
      }

      const editNotes = (rowData) => {
        if (isFunction(notesModalActions.openModal)) {
          notesModalActions.openModal(rowData);
        }
      }

      const editDocs = (rowData) => {
        if (isFunction(docsModalActions.openModal)) {
          docsModalActions.openModal(rowData);
        }
      }

      const getDocsCount = (rowData) => {
        const allFiles = rowData.files.filter((file) => !file.archived.value);
        return allFiles.length;
      }

      const excludeInternalDelFields = (id) => {
        return ['unscheduled', 'underDelivered'].includes(id);
      }

      const closeDeliveryItems = async (params) => {
        state.isDeliveryItemsActive = false;
      };

      const refreshCardData = async () => {
        await refreshCard();
      }
  
      return {
        ...toRefs(state),
        getLoadData,
        onTableCellClicked,
        refreshTable,
        getApiMode,
        get,
        eventHandler,
        updateShippingLabel,
        getItemDeliveries,
        notesCount,
        editNotes,
        editDocs,
        getDocsCount,
        excludeInternalDelFields,
        closeDeliveryItems,
        refreshCardData,
      };
    },
  });
  </script>
  <style scoped>
  ::v-deep(.is-pulled-right.buttons button:first-child:hover i) {
    color: white !important;
  }
  </style>
  